import {
  MusicOverride,
  PresenterProfileResponse,
  SlideType,
} from "pages/media/types";
import { AvatarShot, GradientDefinition, TemplateItem } from "types";
import { processTemplateSlideItems } from "./helpers/itemsHelper";

export type Chapter = {
  templateId?: string;
  topic: string;
  sectionId: string;
  type: TemplateType;
};

export enum TemplateType {
  INTRODUCTION = "Introduction",
  TRANSITION = "Transition",
  CHAPTER = "Chapter",
  CLOSING = "Closing",
}

export type HorizontalTextAlign = "left" | "center" | "right";
export type VerticalTextAlign = "top" | "center" | "bottom";

export enum TabKeys {
  // Tab keys shown in the right-panel menu (storyboard + theme editors).
  script = "Script",
  layout = "Layout",
  content = "Content",
  objects = "Objects",
  brand = "Brand",
  avatar = "Avatar",
  motion = "Motion",
  audio = "Audio",
  // Theme-editor-only tab.
  themeInfo = "ThemeInfo",
}

export enum Effects {
  fadeIn = "fadeIn",
  fadeOut = "fadeOut",
  enterFromTop = "enterFromTop",
  enterFromBottom = "enterFromBottom",
  enterFromLeft = "enterFromLeft",
  enterFromRight = "enterFromRight",
  leaveToTop = "leaveToTop",
  leaveToBottom = "leaveToBottom",
  leaveToLeft = "leaveToLeft",
  leaveToRight = "leaveToRight",
}

export enum MoveMethod {
  Linear = "Linear",
  EaseInOut = "EaseInOut",
  Elastic = "Elastic",
  Exponential = "Exponential",
  Bounce = "Bounce",
  EaseInOutExpo = "EaseInOutExpo",
  EaseInOutQuint = "EaseInOutQuint",
}

export enum FadeMethod {
  Linear = "Linear",
  EaseInOut = "EaseInOut",
  Exponential = "Exponential",
}

export type AnimationConfig = {
  enabled: boolean;
  duration?: number;
  method?: MoveMethod | FadeMethod;
  delay?: number;
};

export type AnimationEffectValue = boolean | AnimationConfig;

export type AnimationEffects = Partial<Record<Effects, AnimationEffectValue>>;

export type BodyView = "Color" | "Picture" | "Video";
export type HeaderView = Omit<BodyView, "Video">;

export type Position = {
  x: number;
  y: number;
};

export type Size = {
  width: number;
  height: number;
};

export type NullableHeightSize = {
  width: number;
  height: number | null;
};

export type Stroke = {
  width?: number | null;
  color?: string | null;
  radius?: number | null;
};

/**
 * Crop mask of a media item: the visible sub-rectangle of the item's box,
 * expressed as fractions of that box (0…1). The media always fills the box, so
 * the mask is always inside it. Default (no crop) is the full box {0,0,1,1};
 * the area outside the mask is hidden in the final render and dimmed while
 * editing. Serializes to a Rectangle mask on the graphics.
 */
export type Mask = {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Crop shape. Defaults to a rectangle; "ellipse" renders a circle/ellipse. */
  shape?: "rectangle" | "ellipse";
};

export enum AvatarShape {
  Regular = "regular",
  Circle = "circle",
  Box = "box",
}

export type Avatar = {
  presenter?: PresenterProfileResponse;
  avatarShot: AvatarShot;
  size: Size;
  position: Position;
  contactId?: string;
  animations?: AnimationEffects;
  avatarShape: AvatarShape;
  background?: string | GradientDefinition;
  stroke?: Stroke;
  layerIndex: number;
  rotation?: number;
};

export type MediaItem = {
  id: string;
  assetId: string;
  position: Position;
  size: Size;
  path?: string;
  animations?: AnimationEffects;
  stroke?: Stroke;
  layerIndex: number;
  rotation?: number;
  /** Optional crop mask (visible sub-rectangle of the item box). */
  mask?: Mask;
};

// Asset kind from the API — matches slide.placeholders.image_element.media_type.
// Distinct from the capitalized `type` discriminator on *ItemWithType, which
// references ITEM_TYPES (canvas item kind).
export type MediaKind = "image" | "video";

export type MainImage = MediaItem & {
  visible: boolean;
  // Overrides slide.placeholders.image_element.media_type when the user
  // replaces the main media with an asset of a different kind (image↔video).
  mediaType?: MediaKind;
};

export type VideoScene = {
  enable: boolean;
  assetPath?: string;
  assetId?: string;
  view: BodyView;
  /** User-set speech duration in frames (25fps). Undefined when untouched. */
  duration?: number;
  /** Natural duration of the asset in frames (25fps). Display-only, not sent to BE. */
  assetDuration?: number;
  speechType?: string;
  /** Intro/outro only: play the video's own soundtrack as speech (music muted, no loop). */
  playVideoSound?: boolean;
};

export const ITEM_TYPES = {
  Image: "Image",
  Subtitles: "Subtitles",
  Title: "Title",
  Presenter: "Presenter",
  Video: "Video",
  Logo: "Logo",
  Background: "Background",
  CustomText: "CustomText",
  Shape: "Shape",
  ParameteredText: "ParameteredText",
  Table: "Table",
} as const;

export type ItemType = {
  type: (typeof ITEM_TYPES)[keyof typeof ITEM_TYPES];
  id?: string;
  orientation?: LineOrientation;
  shapeType?: ShapeType;
};

export type CommonTextProperties = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  fontSize: number;
  fontPath?: string;
  fontAssetId?: string;
  color?: string;
  strokeColor?: string;
  backgroundColor?: string;
  position: Position;
  size: Size;
  animations?: AnimationEffects;
  textAlign: HorizontalTextAlign;
  lineHeight: number;
  layerIndex: number;
  wrapText?: boolean;
  upperCase?: boolean;
  verticalAlign?: VerticalTextAlign;
  adjustFont?: boolean;
  rotation?: number;
};

export type SubtitlesItem = Omit<CommonTextProperties, "size"> & {
  id?: string;
  showSubtitles: boolean;
  animated: boolean;
  size: NullableHeightSize;
};

export type TitleItem = Omit<CommonTextProperties, "size"> & {
  id?: string;
  showTitle: boolean;
  size: NullableHeightSize;
};

/**
 * @description Text content can be found in the slides fetched from the server, using the slide id and path.
 */
export type ParameteredText = Omit<CommonTextProperties, "size"> & {
  id: string;
  path: string;
  index?: number;
  animated: boolean;
  content: string;
  size: NullableHeightSize;
  visible: boolean;
};

export type CustomText = CommonTextProperties & {
  id: string;
  content: string;
};

export type Logo = {
  showLogo: boolean;
  assetId?: string | null;
  path?: string;
  position: Position;
  size: Size;
  layerIndex: number;
  rotation?: number;
};

export type Header = {
  view: HeaderView;
  position: Position;
  size: Size;
  background?: string | GradientDefinition;
  banner?: {
    assetId?: string;
    path?: string;
  };
  layerIndex: number;
};

export const SHAPE_TYPES = {
  RECTANGLE: "rectangle",
  CIRCLE: "circle",
  LINE: "line",
} as const;

export type ShapeType = (typeof SHAPE_TYPES)[keyof typeof SHAPE_TYPES];

export const LINE_ORIENTATIONS = {
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
} as const;

export type LineOrientation =
  (typeof LINE_ORIENTATIONS)[keyof typeof LINE_ORIENTATIONS];

export type Shape = {
  id: string;
  shapeType: ShapeType;
  size: Size;
  position: Position;
  animations?: AnimationEffects;
  background: string | GradientDefinition;
  layerIndex: number;
  stroke?: Stroke;
  orientation?: LineOrientation;
  rotation?: number;
};

export type TableTextStyle = Omit<
  CommonTextProperties,
  "position" | "size" | "layerIndex"
> & {
  animated: boolean;
  position: Position;
  size: NullableHeightSize;
};

export type TableDividerStyle = {
  size: Size;
  position: Position;
  background: string | GradientDefinition;
  orientation: LineOrientation;
  animations?: AnimationEffects;
};

export type Table = {
  id: string;
  position: Position;
  size: Size;
  background?: string;
  layerIndex: number;
  textStyle: TableTextStyle;
  showDividers: boolean;
  dividerStyle: TableDividerStyle;
  animationRowDelay?: number;
  rotation?: number;
};

export type Figure = {
  number: ParameteredText;
  units?: ParameteredText;
};

export type FigureType = "number" | "units";

export type BodyBackground = {
  id?: string;
  bodyColor?: string | GradientDefinition;
  bodyImage?: string;
  bodyImageAsset?: string;
  bodyVideo?: string;
  bodyVideoAsset?: string;
  bodyView: BodyView;
};

export type TemplateState = {
  textStyle: SubtitlesItem;
  presenter: Avatar; // Can be overwritten on applying a public/custom theme
  titleStyle: TitleItem;
  intro: VideoScene;
  transition: VideoScene; // Can be overwritten on applying a public/custom theme
  closing: VideoScene;
  images: MediaItem[];
  videos: MediaItem[];
  shapes: Shape[];
  customTexts: CustomText[];
  logo: Logo; // Can be overwritten on applying a public/custom theme
  header: Header; // Can be overwritten on applying a public/custom theme
  background: BodyBackground; // Can be overwritten on applying a public/custom theme
  currentItem?: ItemType;
  music?: MusicOverride;
  /* Slides items, new and existing items that are separate so they can be overwritten when applying a public/custom theme */
  parameteredTexts?: ParameteredText[];
  // path is found in the slides fetched from the server as image_element placeholder
  mainImage?: MainImage;
  figure?: Figure;
  slideLayoutShapes?: Shape[];
  slideLayoutType?: SlideLayout;
  table?: Table;
};

export type Templates = Map<string, TemplateState>;

export type Slot = {
  path?: string;
  parameteredTextItem?: TemplateItemWithLayerIndex;
  mainImageItem?: TemplateItemWithLayerIndex;
  figureNumberItem?: TemplateItemWithLayerIndex;
  figureUnitsItem?: TemplateItemWithLayerIndex;
  tableItem?: ReturnType<typeof processTemplateSlideItems>["tableItem"];
};

export type MappedSlot = {
  parameteredText?: ParameteredText;
  mainImage?: MainImage;
  figure?: {
    number: ParameteredText;
    units?: ParameteredText;
  };
  table?: Table;
};

export enum MediaAssetsViewOptions {
  Document = "doc",
  Stock = "stock",
  AIGenerated = "ai_gen",
  Uploads = "upload",
}

export enum MediaAssetTabs {
  Images = "images",
  Videos = "videos",
}

export type TemplateItemWithId = TemplateItem & {
  id: string;
};

export type TemplateItemWithLayerIndex<T = TemplateItem> = T & {
  layerIndex: number;
};

export type ItemWithLayerIndex = {
  item: TemplateItem | TemplateItem[];
  layerIndex: number;
};

export enum SlideLayout {
  TITLE = "title",
  IMAGE = "image",
  BULLET_POINT = "bullet_point",
  FIGURE = "figure",
  DOUBLE_PARAGRAPH = "double_paragraph",
  PARAGRAPH_IMAGE = "paragraph_image",
  QUOTE = "quote",
}

export const SLIDE_TYPE_TO_LAYOUT: Record<SlideType, SlideLayout> = {
  title: SlideLayout.TITLE,
  image: SlideLayout.IMAGE,
  bullet_point: SlideLayout.BULLET_POINT,
  figure: SlideLayout.FIGURE,
  double_paragraph: SlideLayout.DOUBLE_PARAGRAPH,
  paragraph_image: SlideLayout.PARAGRAPH_IMAGE,
  quote: SlideLayout.QUOTE,
};

export const SLIDE_LAYOUT_SHAPE_NAMES = [
  "transition_overlay",
  "secondary_background",
];
export const SLIDE_LAYOUT_SHAPE_PARTIAL_NAMES = ["divider", "slide_shape"];

export type CopyPasteItem =
  | ShapeItemWithType
  | ImageItemWithType
  | VideoItemWithType
  | CustomTextItemWithType
  | LogoItemWithType;

export type ShapeItemWithType = Shape & { type: typeof ITEM_TYPES.Shape };
export type ImageItemWithType = MediaItem & { type: typeof ITEM_TYPES.Image };
export type VideoItemWithType = MediaItem & { type: typeof ITEM_TYPES.Video };
export type CustomTextItemWithType = CustomText & {
  type: typeof ITEM_TYPES.CustomText;
};
export type LogoItemWithType = Logo & { type: typeof ITEM_TYPES.Logo };

export type StrokeableItem = MediaItem | Shape;

export type ResizeHandlePosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "topLeft"
  | "topRight"
  | "bottomLeft"
  | "bottomRight";
